<template>
  <!-- Rendered inside an ion-popover on desktop and an ion-modal sheet on
       mobile, so it owns no chrome of its own — just the rows. -->
  <ion-list lines="none" class="settings-menu ion-no-padding">
    <template v-if="view === 'root'">
      <ion-item
        v-if="appStore.isAuthenticated"
        class="account-row"
        lines="full"
      >
        <ion-avatar slot="start" class="account-avatar" aria-hidden="true">
          <img
            v-if="picture"
            :src="picture"
            alt=""
            referrerpolicy="no-referrer"
          />
          <ion-icon aria-hidden="true" v-else :icon="personCircleOutline" />
        </ion-avatar>
        <ion-label>
          <h3 class="account-name">{{ appStore.currentUser?.name }}</h3>
          <!-- Absent for a username/password account, whose name is the whole
               identity it has (docs/architecture/auth-modes.md). -->
          <p v-if="appStore.currentUser?.email" class="account-email">
            {{ appStore.currentUser.email }}
          </p>
        </ion-label>
      </ion-item>

      <ion-item :button="true" :detail="true" @click="view = 'language'">
        <ion-icon :icon="globeOutline" slot="start" aria-hidden="true" />
        <ion-label>{{ $t("menu.language") }}</ion-label>
        <ion-note slot="end">{{ appStore.currentLanguage.fullName }}</ion-note>
      </ion-item>

      <!-- Theme is a binary, so it toggles in place: a submenu for two states
           would cost a click and teach nothing. -->
      <ion-item>
        <ion-icon
          :icon="appStore.isDarkMode ? moonOutline : sunnyOutline"
          slot="start"
          aria-hidden="true"
        />
        <ion-toggle
          :checked="appStore.isDarkMode"
          @ion-change="appStore.setDarkMode($event.detail.checked)"
        >
          {{ $t("menu.darkMode") }}
        </ion-toggle>
      </ion-item>

      <!-- Signed-out visitors keep the footer's link straight to GitHub Issues;
           the in-app form needs a session to attribute the report. -->
      <ion-item
        v-if="appStore.isAuthenticated"
        :button="true"
        :detail="false"
        class="report-row"
        @click="goToReport"
      >
        <ion-icon :icon="bugOutline" slot="start" aria-hidden="true" />
        <ion-label>{{ $t("menu.reportProblem") }}</ion-label>
      </ion-item>

      <!-- Signed out too: the guide is public, and someone deciding whether to
           play is exactly who wants to read the rules first. -->
      <ion-item
        :button="true"
        :detail="false"
        class="user-guide-row"
        @click="goToUserGuide"
      >
        <ion-icon :icon="bookOutline" slot="start" aria-hidden="true" />
        <ion-label>{{ $t("menu.userGuide") }}</ion-label>
      </ion-item>

      <ion-item
        :button="true"
        :detail="false"
        lines="full"
        class="auth-row"
        @click="onAuth"
      >
        <ion-icon
          :icon="appStore.isAuthenticated ? logOutOutline : logInOutline"
          slot="start"
          aria-hidden="true"
        />
        <ion-label>
          {{ appStore.isAuthenticated ? $t("nav.signOut") : $t("nav.signIn") }}
        </ion-label>
      </ion-item>
    </template>

    <template v-else>
      <ion-item
        :button="true"
        :detail="false"
        lines="full"
        @click="view = 'root'"
      >
        <ion-icon :icon="chevronBackOutline" slot="start" aria-hidden="true" />
        <ion-label>{{ $t("menu.language") }}</ion-label>
      </ion-item>

      <ion-item
        v-for="lang in appStore.availableLanguages"
        :key="lang.code"
        :button="true"
        :detail="false"
        @click="selectLanguage(lang.code)"
      >
        <ion-label>{{ lang.label }} {{ lang.fullName }}</ion-label>
        <ion-icon
          v-if="appStore.isLanguage(lang.code)"
          :icon="checkmarkOutline"
          slot="end"
          aria-hidden="true"
        />
      </ion-item>
    </template>
  </ion-list>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import { useRouter } from "vue-router";
import {
  IonAvatar,
  IonIcon,
  IonItem,
  IonLabel,
  IonList,
  IonNote,
  IonToggle,
} from "@ionic/vue";
import {
  bookOutline,
  bugOutline,
  checkmarkOutline,
  chevronBackOutline,
  globeOutline,
  logInOutline,
  logOutOutline,
  moonOutline,
  personCircleOutline,
  sunnyOutline,
} from "ionicons/icons";
import { useAppStore } from "@/stores/app";

const emit = defineEmits<{ close: [] }>();

const router = useRouter();
const appStore = useAppStore();

function goToReport() {
  emit("close");
  router.push("/report");
}

// Opens the written guide, not the tour. Asking for the guide should hand over
// something you can read at your own pace; replaying the tour is a deliberate
// second choice, offered by a button at the end of that page.
function goToUserGuide() {
  emit("close");
  router.push("/guide");
}

// A nested list rather than an inline segment: adding a locale to
// AVAILABLE_LANGUAGES then costs no layout work here.
const view = ref<"root" | "language">("root");

const picture = computed(() => appStore.currentUser?.picture);

function selectLanguage(code: string) {
  appStore.setLanguage(code);
  view.value = "root";
  emit("close");
}

// The menu closes first: both modals are siblings of the popover/sheet, and
// Ionic will not stack them reliably if the trigger is still open.
function onAuth() {
  emit("close");
  if (appStore.isAuthenticated) {
    appStore.openLogoutModal();
  } else {
    appStore.openLoginModal();
  }
}
</script>

<style scoped>
.settings-menu ion-item {
  font-size: 0.875rem;
  --min-height: 44px;
}

.account-row {
  --min-height: 60px;
}

.account-avatar {
  width: 2rem;
  height: 2rem;
  color: var(--ion-color-medium);
}

.account-avatar ion-icon {
  font-size: 2rem;
}

.account-name {
  font-size: 0.875rem;
  font-weight: 600;
  margin: 0;
}

.account-email {
  font-size: 0.75rem;
  color: var(--ion-color-medium);
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
}

.auth-row {
  --border-width: 1px 0 0 0;
}
</style>
